<script lang="ts">
export interface Props {
  continueAction?: string;
  continueActionParams?: any; 
};
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import UiCodeInput from '@/components/UIElements/UiCodeInput.vue';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import eventBus from '@/setup/eventBus';
import _ from '@/setup/lodash';
import { isErrorResponse, type FormErrors, type VuePropType } from '@/types/core';

const props = withDefaults(defineProps<Props>(), {
  continueAction: '',
  continueActionParams: () => {},
});

const store = useTypedStore();
const router = useRouter();
const api = useApi();

const isOpen = ref(false);
const isLoading = ref(false);
const errors = ref<FormErrors>({});

const isRouteLocked =  computed(() => {
  return store.getters['session/significantActionsSeconds'] === 0
    && ['teams', 'settings'].includes(store.state.nav.mainView);
});

const open = () => {
  isOpen.value = true;
};

const close = ({ isConfirm = false } = {}) => {
  isOpen.value = false;
  errors.value = {};

  if (!isConfirm && ['teams', 'settings'].includes(store.state.nav.mainView)) {
    router.push('/affiliates/list');
  }
};

const keyupHandler = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
};

const confirm: VuePropType<typeof UiCodeInput, 'onComplete'> = async (code) => {
  isLoading.value = true;
  const payload = {
    token: code,
  };
  errors.value = {};

  try {
    await store.dispatch('session/getSessionAuth');
    await api.significantUnlock(payload);
    await store.dispatch('session/updateSession');
    await close({ isConfirm: true });
    if (props.continueAction) {
      // @ts-expect-error event теперь типизирован
      eventBus.$emit(props.continueAction, props.continueActionParams);
    }
  } catch (error: unknown) {
    if (isErrorResponse(error)) {
      errors.value = error.data.errors;
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  window.addEventListener('keyup', keyupHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('keyup', keyupHandler);
});

defineExpose({
  open,
});
</script>

<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="two-factor-popup"
    >
      <div
        v-if="isRouteLocked"
        class="bg"
      />
      <div class="popup">
        <div class="head">
          <span class="name">{{ $t('loginView.tfa.title') }}</span>
          <UiIcon
            :size="20"
            class="close"
            name="times"
            @click="close"
          />
        </div>
        <div class="body">
          <div
            class="title"
            v-html="$t('loginView.tfa.message_action')"
          />
          <UiCodeInput
            :field-width="55"
            :field-height="55"
            class="tfa-input"
            auto-focus
            @complete="confirm"
          />
          <div
            v-if="!_.isEmpty(errors)"
            class="error"
          >
            {{ errors.token?.[0]?.message }}
          </div>
        </div>
      </div>
      <div class="wrapper" />
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.two-factor-popup {
  .tfa-code-input{
    text-align: center;
  }
  .bg {
    position: fixed;
    top: 54px;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fff;
  }
}
</style>
